export const FEED_TYPES = {
  MEDIA_AUDIO: 'audio',
  MEDIA: 'media',
  PRESENTER: 'presenter',
  EMPTY: 'empty',
  EMAIL: 'email',
  SCREENSHARE: 'screenshare',
  SIP_CALL: 'sipCall',
  SRT_STREAM: 'srtStream',
  ONSITE_LOCATION: 'onsiteLocation',
} as const;

export const PLAY_STATES = {
  PLAYING: 0,
  ENDING: 1,
  LATE: 2,
};

export enum SHOT_THUMBNAIL_TYPES {
  SHOWBOARD = 1,
  SHOTS_TAB = 2,
  SHOW_DETAILS = 3,
  TELEPROMPTER = 4,
}

export interface ShotFeed {
  media: number | null; // id of media
  presenter: number | null; // brandlive profile
  email: string | null; // used when settings someone in a shot that hasnt made a bl profile yet
  screenshare: string | null; // email for user in a shot who is screensharing
  sipStreamId: string | null;
  srtStreamId?: number | null;
  onsiteLocationId?: number | null;
  feedType: string;
  position: number; // which slot this feed goes in
  presenterDeleted?: boolean;
  audioOnly?: boolean;
  expanded?: boolean;
}

export const baseShotFeed: ShotFeed = {
  media: null,
  presenter: null,
  email: null,
  screenshare: null,
  sipStreamId: null,
  srtStreamId: null,
  onsiteLocationId: null,
  feedType: FEED_TYPES.EMPTY,
  position: 0,
};

export interface ShotData {
  // the layout chosen when creating the shot
  forcedLayout: string;
  // the title of a shot
  shotTitle: string;
  // if titles are enabled for the shot as a whole (false means no titles at all)
  videoTitlesOn: boolean;
  shotFeeds: ShotFeed[];
  shotDuration: number; // the expected time length of the shot in seconds
  order: number; // position of this shot in the showboard
  cue: string; // user input
  note: string; // user input
  shotGraphics?: Array<number>;
  autoplay: boolean;
  autoDuration: boolean;
  media?: number;
}

export interface Shot {
  shot_id: number; // unique key
  created_by: number; // user id of creator
  edited_by: number; // user id of last to edit shot
  meeting: number; // meeting shot corresponds to
  shot_style: {
    queue: AppSettings;
    applied: AppSettings;
  };
  shot_data: ShotData;
}

export interface AppSettings {
  // gr_canvas_style table id
  id?: number;
  // layout
  gap: number;
  layout: string;
  // background
  backgroundColor: string;
  backgroundType: 'image' | 'video' | 'color';
  backgroundImage: string;
  backgroundVideo: string;
  backgroundFill: 'cover' | 'contain';
  backgroundScale: number;
  // wallpaper
  wallpaperType: 'image' | 'video' | 'color';
  wallpaperImage: string;
  wallpaperColor: string;
  wallpaperScale: number;
  // spacing
  layoutSpacingPresets: Array<LayoutSpacingPreset>;
  spacingLinked: boolean;
  topSpacing: number;
  bottomSpacing: number;
  sideSpacing: number;
  innerSpacing: number;
  // borders
  presenterVideoRadius: number;
  presenterVideoBorderOn: boolean;
  presenterVideoBorderPosition: 'center' | 'inside' | 'outside';
  presenterVideoBorderColor: string;
  presenterVideoBorderOpacity: number;
  presenterVideoBorderSize: number;
  // titles
  titlesOn: boolean;
  titlesUseName: boolean;
  titlesUseTitle: boolean;
  titlesUseLocation: boolean;
  titlesAnimation: string;
  titlesDuration: number | null;
  titlesPosition: string;
  titlesFontColor1: string;
  titlesFontColor2: string;
  titlesBackgroundColor1: string;
  titlesBackgroundColor2: string;
  titlesBackgroundColor3: string;
  titlesScale: number;
  titlesSelectedCard: number;
}

export interface LayoutSpacingPreset {
  layout: string;
  isLinked: boolean;
  spacing: LayoutSpacing;
}

export type LayoutSpacingKey = 'top' | 'bottom' | 'side' | 'inner';

export type LayoutSpacing = {
  [key in LayoutSpacingKey]: number;
};
